import { BASE_API_URL } from '@env';
import { useUserStore } from '~providers/userStore/zustand';

export interface ApiRequestConfig extends RequestInit {
  skipAuth?: boolean;
  timeout?: number;
}

class ApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = BASE_API_URL;
  }

  private getAuthHeaders(): Record<string, string> {
    const token = useUserStore.getState().token;
    if (!token) {
      console.warn('No auth token available for API request');
      return {};
    }

    // Token is already in the correct format (PYXI_xxx)
    return {
      'Authorization': token,
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    config: ApiRequestConfig = {}
  ): Promise<T> {
    const { skipAuth = false, timeout = 10000, ...requestConfig } = config;
    
    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...requestConfig.headers,
    };

    // Add auth headers if not skipped
    if (!skipAuth) {
      const authHeaders = this.getAuthHeaders();
      Object.assign(headers, authHeaders);
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...requestConfig,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      
      throw error;
    }
  }

  // GET request
  async get<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: any, config?: ApiRequestConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string, config?: ApiRequestConfig): Promise<T> {
    return this.makeRequest<T>(endpoint, { ...config, method: 'DELETE' });
  }

  // GET request with query parameters
  async getWithParams<T>(
    endpoint: string,
    params: Record<string, any>,
    config?: ApiRequestConfig
  ): Promise<T> {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });

    const queryString = searchParams.toString();
    const fullEndpoint = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.get<T>(fullEndpoint, config);
  }

  // Upload file (multipart/form-data)
  async uploadFile<T>(
    endpoint: string,
    formData: FormData,
    config?: ApiRequestConfig
  ): Promise<T> {
    const { skipAuth = false, timeout = 30000, ...requestConfig } = config || {};
    
    const url = `${this.baseURL}${endpoint}`;
    
    // Prepare headers (don't set Content-Type for FormData)
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...requestConfig.headers,
    };

    // Add auth headers if not skipped
    if (!skipAuth) {
      const token = useUserStore.getState().token;
      if (token) {
        headers['Authorization'] = token;
      }
    }

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...requestConfig,
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return response.json();
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        throw new Error('Upload timeout');
      }
      
      throw error;
    }
  }
}

// Create singleton instance
export const apiService = new ApiService();

// Export default
export default apiService;
